import type { ReactNode } from 'react';
import { Link, Navigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ArrowRightIcon, CheckIcon } from '@phosphor-icons/react';
import { SEED_PLANS, type FeatureKey } from '@growhero/shared';
import { LogoMark } from '@growhero/brand';
import { useTenant } from '@/tenant/TenantProvider';
import { usePlans } from '@/features/billing/api';
import { formatNumber } from '@/lib/format';
import { useVatLabels } from '@/features/billing/vat';
import { Button, Illustration, PageHeader, ProMark, type IllustrationName } from '@/ui';
import { ReviewsSectionNav } from '@/features/reviews/components/ReviewsSectionNav';
import { portalNav } from './nav';

/** Feature → copy key in the `pro` namespace. Keys without copy fall back to `generic`. */
const COPY: Partial<Record<FeatureKey, string>> = {
  segments: 'segments',
  automations: 'automations',
  'ai.assistant': 'assistant',
  'schema.custom': 'data',
  'google.business': 'google',
  insights: 'insights',
  'dashboard.codegen': 'dashboard',
  'ai.copy': 'copy',
  'ai.onboarding': 'onboarding',
  'forms.interest': 'formsInterest',
  'forms.events': 'formsEvents',
};

/** Each upsell shows its own feature in the side panel, not one shared padlock. */
const ART: Partial<Record<FeatureKey, IllustrationName>> = {
  segments: 'contacts',
  automations: 'sprout',
  'ai.assistant': 'inbox',
  'schema.custom': 'search',
  'google.business': 'reviews',
  insights: 'chart',
  'dashboard.codegen': 'chart',
  'ai.copy': 'messages',
  'ai.onboarding': 'sprout',
  'forms.interest': 'messages',
  'forms.events': 'sprout',
};

/**
 * Locked pages that are not top-level nav items still get their section's page header (and, for
 * the reviews section, its tab bar), so the upsell sits in the same scaffolding as the real page.
 */
function SectionHeader({ feature }: { feature: FeatureKey }) {
  const { t } = useTranslation(['nav', 'reviews', 'dashboards']);
  if (feature === 'google.business')
    return (
      <PageHeader
        eyebrow={t('reviews:section')}
        title={t('reviews:pages.google.title')}
        meta={<ProMark />}
      >
        <ReviewsSectionNav />
      </PageHeader>
    );
  if (feature === 'dashboard.codegen')
    return (
      <PageHeader
        back={{ to: '/', label: t('dashboards:studio.back') }}
        eyebrow={t('dashboards:studio.eyebrow')}
        title={t('dashboards:studio.title')}
        meta={<ProMark />}
      />
    );
  const navItem = portalNav.flatMap((g) => g.items).find((i) => i.feature === feature);
  if (!navItem) return null;
  const group = portalNav.find((g) => g.items.includes(navItem));
  return (
    <PageHeader
      eyebrow={group?.labelKey ? t(`nav:${group.labelKey}`) : undefined}
      title={t(`nav:${navItem.labelKey}`)}
      meta={<ProMark />}
    />
  );
}

/**
 * Upsell panel shown in place of a Pro-only page for Basic tenants (ARCHITECTURE §2: never hide
 * navigation: it's our upsell surface). A white card with the pitch and the saffron upgrade CTA,
 * next to a blue brand-field panel showing the feature's illustration. Not a modal nag.
 */
export function ProLock({
  feature,
  compact,
  withHeader,
}: {
  feature: FeatureKey;
  compact?: boolean;
  withHeader?: boolean;
}) {
  const { t } = useTranslation(['pro', 'nav']);
  const { current } = useTenant();
  const key = COPY[feature] ?? 'generic';
  // Live catalog (Super Admin can change price/allowances); seed values only while it loads.
  const plansQ = usePlans();
  const seed = SEED_PLANS.find((p) => p.key === 'pro')!;
  const live = plansQ.data?.plans.find((p) => p.key === 'pro');
  const pro = {
    priceCents: live?.priceCents ?? seed.priceCents,
    currency: live?.currency ?? seed.currency,
    allowances: {
      ai: live?.allowances.ai ?? seed.allowances.ai,
      sms: live?.allowances.sms ?? seed.allowances.sms,
    },
  };
  const price = useVatLabels(plansQ.data?.vatRateBps)(pro.priceCents, pro.currency);
  const canUpgrade = !current || current.role !== 'member';
  const points = t(`features.${key}.points`, { returnObjects: true }) as string[];

  if (compact) {
    return (
      <div className="flex flex-col gap-3 rounded-lg border border-accent/35 bg-accent-soft/60 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <ProMark className="mt-0.5" />
          <div>
            <div className="font-bold text-ink">{t(`features.${key}.title`)}</div>
            <div className="text-sm text-ink-muted">{t(`features.${key}.lede`)}</div>
          </div>
        </div>
        {canUpgrade && (
          <Button asChild size="sm" variant="secondary">
            <Link to="/billing?upgrade=pro">{t('cta')}</Link>
          </Button>
        )}
      </div>
    );
  }

  return (
    <>
      {withHeader && <SectionHeader feature={feature} />}
      <section
        className="animate-rise overflow-hidden rounded-lg border border-hairline bg-surface shadow-sm"
        aria-labelledby="prolock-title"
      >
        <div className="grid lg:grid-cols-[1.25fr_1fr]">
          <div className="p-7 sm:p-10">
            <div className="flex items-center gap-2.5">
              <ProMark />
              <span className="eyebrow">{t('eyebrow')}</span>
            </div>
            <h2
              id="prolock-title"
              className="display-heavy mt-5 max-w-[20ch] text-[2rem] leading-[1.08] text-ink sm:text-[2.375rem]"
            >
              {t(`features.${key}.title`)}
            </h2>
            <p className="mt-4 max-w-[52ch] text-[0.9375rem] leading-relaxed text-ink-muted">
              {t(`features.${key}.lede`)}
            </p>

            {Array.isArray(points) && points.length > 0 && (
              <ul className="mt-7 grid gap-3.5">
                {points.map((p, i) => (
                  <li key={i} className="flex items-start gap-3 text-[0.9375rem] text-ink-soft">
                    <span className="mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-primary-soft text-primary-ink">
                      <CheckIcon weight="bold" className="size-3" />
                    </span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-9 flex flex-wrap items-center gap-x-5 gap-y-3 border-t border-hairline pt-6">
              {canUpgrade ? (
                <>
                  {/* The one action of this screen: saffron (A3 CTA). */}
                  <Button asChild size="lg" variant="accent">
                    <Link to="/billing?upgrade=pro">
                      {t('cta')}
                      <ArrowRightIcon weight="bold" />
                    </Link>
                  </Button>
                  <div className="text-sm leading-snug text-ink-muted">
                    {/* Net price «39 € + ΦΠΑ», gross on its own line (ADR-B10). */}
                    <span className="num text-xl font-extrabold tracking-headline text-ink">
                      {price.net}
                    </span>
                    <span>
                      {' '}
                      {t('common:vat.plusVatShort')} / {t('perMonth')}
                    </span>
                    <div className="num text-xs text-ink-subtle">{price.withVat}</div>
                    <div>
                      {t('includes', {
                        ai: formatNumber(pro.allowances.ai),
                        sms: formatNumber(pro.allowances.sms),
                      })}
                    </div>
                  </div>
                </>
              ) : (
                <p className="text-sm text-ink-muted">{t('askOwner')}</p>
              )}
            </div>
            {/* The reassurance, once and small, right under the call to action. */}
            <p className="mt-4 max-w-[60ch] text-xs leading-relaxed text-ink-muted">
              {t('reassure')} {t('reassureNote')}
            </p>
          </div>

          <aside
            className="surface-field relative flex flex-col justify-center overflow-hidden p-7 sm:p-10"
            data-feature-art={ART[feature] ?? 'lock'}
            data-surface="field"
          >
            {/* Tonal laurel watermark: the brand signature behind the feature art. */}
            <LogoMark
              size={360}
              tone="mono"
              title={null}
              className="pointer-events-none absolute -bottom-24 -right-24 text-field-tonal"
            />
            <div className="relative grid place-items-center">
              <Illustration name={ART[feature] ?? 'lock'} className="w-72 max-w-full xl:w-80" />
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}

/** Render children only when the tenant's plan includes `feature`; otherwise the ProLock spread. */
export function FeatureGate({
  feature,
  children,
  fallback,
}: {
  feature: FeatureKey;
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const { hasFeature } = useTenant();
  if (hasFeature(feature)) return <>{children}</>;
  return <>{fallback ?? <ProLock feature={feature} withHeader />}</>;
}

/**
 * Renders children only when `feature` exists on this deployment. A platform-switched feature
 * that the server has not turned on (e.g. `google.business`, ADR-G7) redirects to `to` instead:
 * hidden features get no page and no upsell.
 */
export function PlatformFeatureGate({
  feature,
  to,
  children,
}: {
  feature: FeatureKey;
  to: string;
  children: ReactNode;
}) {
  const { isOnPlatform } = useTenant();
  if (!isOnPlatform(feature)) return <Navigate to={to} replace />;
  return <>{children}</>;
}
